import { useSeo } from "@/lib/seo";
import { motion } from "framer-motion";
import { Mail, MapPin, MessageCircle, Clock } from "lucide-react";

export default function Contact() {
    useSeo({
        title: "Contact — Coway",
        description:
            "Contactez l'équipe Coway. Support, presse, partenariats — nous répondons sous 48h ouvrées.",
        canonical: "https://coway.fr/contact",
    });

    return (
        <section
            className="pt-32 lg:pt-40 pb-24"
            data-testid="contact-page"
        >
            <div className="max-w-5xl mx-auto px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="text-xs text-coway uppercase tracking-[0.3em] font-semibold mb-5">
                        Contact
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-slate-900 max-w-3xl">
                        Une question ?
                        <br />
                        <span className="text-slate-400">Nous sommes là.</span>
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
                        Support utilisateur, presse, partenariats — l'équipe
                        Coway lit chaque message et vous répond personnellement
                        sous 48 heures ouvrées.
                    </p>
                </motion.div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <motion.a
                        href="mailto:cowayfrance@gmail.com"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        whileHover={{ y: -6 }}
                        data-testid="contact-email-card"
                        className="group p-8 lg:p-10 rounded-3xl bg-white border border-slate-200 hover:border-coway/40 transition-colors card-elevated block relative overflow-hidden"
                    >
                        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-coway/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                        <div className="relative z-10">
                            <span className="inline-flex w-12 h-12 rounded-2xl bg-coway-soft border border-coway/15 items-center justify-center mb-6">
                                <Mail
                                    className="w-5 h-5 text-coway"
                                    strokeWidth={1.8}
                                />
                            </span>
                            <h3 className="font-display text-2xl font-bold text-slate-900">
                                Écrivez-nous
                            </h3>
                            <p className="mt-2 text-slate-500 text-sm leading-relaxed">
                                Le moyen le plus rapide d'obtenir une réponse
                                personnelle de l'équipe Coway.
                            </p>
                            <p className="mt-6 font-display text-xl font-bold text-coway break-all">
                                cowayfrance@gmail.com
                            </p>
                        </div>
                    </motion.a>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="p-8 lg:p-10 rounded-3xl bg-white border border-slate-200 card-elevated"
                        data-testid="contact-info-card"
                    >
                        <span className="inline-flex w-12 h-12 rounded-2xl bg-slate-50 border border-slate-200 items-center justify-center mb-6">
                            <MapPin
                                className="w-5 h-5 text-slate-700"
                                strokeWidth={1.8}
                            />
                        </span>
                        <h3 className="font-display text-2xl font-bold text-slate-900">
                            Informations
                        </h3>
                        <div className="mt-6 space-y-4 text-sm">
                            <Row label="Éditeur" value="Coway" />
                            <Row label="Pays" value="France" />
                            <Row label="Email support" value="cowayfrance@gmail.com" />
                            <Row label="Délai de réponse" value="24h ouvrées" />
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="p-8 lg:p-10 rounded-3xl bg-white border border-slate-200 card-elevated md:col-span-2"
                        data-testid="contact-topics-card"
                    >
                        <span className="inline-flex w-12 h-12 rounded-2xl bg-slate-50 border border-slate-200 items-center justify-center mb-6">
                            <MessageCircle
                                className="w-5 h-5 text-slate-700"
                                strokeWidth={1.8}
                            />
                        </span>
                        <h3 className="font-display text-2xl font-bold text-slate-900">
                            Pour quel sujet ?
                        </h3>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {[
                                {
                                    t: "Support utilisateur",
                                    d: "Compte, sécurité, signalement, aide à l'utilisation.",
                                },
                                {
                                    t: "Presse & médias",
                                    d: "Demandes d'interviews, communiqués.",
                                },
                                {
                                    t: "Partenariats",
                                    d: "Marques, opérateurs VTC, taxis.",
                                },
                            ].map((s) => (
                                <div
                                    key={s.t}
                                    className="p-5 rounded-2xl bg-slate-50 border border-slate-100"
                                >
                                    <p className="font-semibold text-slate-900">
                                        {s.t}
                                    </p>
                                    <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
                                        {s.d}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <div className="mt-8 flex items-center gap-3 text-sm text-slate-500">
                            <Clock className="w-4 h-4" strokeWidth={1.6} />
                            Indiquez l'objet de votre demande pour un traitement
                            plus rapide.
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 border-b border-slate-100 pb-3 last:border-0 last:pb-0">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400 sm:w-36">
                {label}
            </span>
            <span className="text-slate-700">{value}</span>
        </div>
    );
}
